import { useState } from "react";
import { Link } from "react-router";
import { keepPreviousData } from "@tanstack/react-query";
import { ArrowRight, Loader2, Printer } from "lucide-react";
import { ExtratoDocumento } from "@bookdental/ui";
import { hojeEmTexto } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { BotaoExportar } from "@/components/shared/BotaoExportar";
import { campo, rotulo } from "@/components/financeiro/estilos";
import { dataCurta } from "@/components/financeiro/extratos/situacao";

// Na impressão só o documento aparece.
const CSS_DE_IMPRESSAO = `
@media print {
  body * { visibility: hidden !important; }
  [data-extrato-impresso], [data-extrato-impresso] * { visibility: visible !important; }
  [data-extrato-impresso] { position: absolute; inset: 0 auto auto 0; width: 100%; padding: 0 !important; border: 0 !important; box-shadow: none !important; }
  @page { margin: 14mm; }
}`;

const ultimoDia = (ano: number, mes0: number) => new Date(Date.UTC(ano, mes0 + 1, 0)).toISOString().slice(0, 10);
function mes(deslocamento: number) {
  const [a, m] = hojeEmTexto().split("-").map(Number);
  const d = new Date(Date.UTC(a!, m! - 1 + deslocamento, 1));
  return { desde: d.toISOString().slice(0, 10), ate: ultimoDia(d.getUTCFullYear(), d.getUTCMonth()) };
}
const ATALHOS = [
  { rotulo: "Este mês", periodo: () => mes(0) },
  { rotulo: "Mês passado", periodo: () => mes(-1) },
];

/** A aba Extrato do cadastro (plano pago): o que foi entregue a este cliente no período, lido na
 *  hora — o documento pra conferir ou mandar em PDF/planilha. O emitido, numerado e com
 *  recebimentos, sai pelo "Emitir extrato". */
export function ExtratoDoCadastroPainel({ partnerId }: { partnerId: string }) {
  const [periodo, setPeriodo] = useState(mes(0));
  const [dentistaId, setDentistaId] = useState<string | null>(null);
  const consulta = trpc.extratoSimples.gerar.useQuery({ partnerId, ...periodo, dentistaId }, { placeholderData: keepPreviousData });
  const podeEmitir = trpc.extratoSimples.podeEmitir.useQuery().data ?? false;
  const dados = consulta.data;
  type Item = NonNullable<typeof dados>["extrato"]["itens"][number];

  return (
    <div className="space-y-4">
      <style>{CSS_DE_IMPRESSAO}</style>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="inline-flex rounded-md border border-zinc-200 bg-white p-0.5 text-sm dark:border-border dark:bg-card">
            {ATALHOS.map((a) => {
              const ativo = JSON.stringify(a.periodo()) === JSON.stringify(periodo);
              return (
                <button
                  key={a.rotulo}
                  type="button"
                  aria-pressed={ativo}
                  onClick={() => setPeriodo(a.periodo())}
                  className={cn("rounded px-3 py-1.5", ativo ? "bg-brand-600 font-semibold text-white" : "text-zinc-600 hover:bg-zinc-50 dark:text-muted-foreground dark:hover:bg-muted")}
                >
                  {a.rotulo}
                </button>
              );
            })}
          </div>
          <label>
            <span className={rotulo}>De</span>
            <input type="date" value={periodo.desde} onChange={(e) => e.target.value && setPeriodo({ ...periodo, desde: e.target.value })} className={cn(campo, "w-40")} />
          </label>
          <label>
            <span className={rotulo}>Até</span>
            <input type="date" value={periodo.ate} onChange={(e) => e.target.value && setPeriodo({ ...periodo, ate: e.target.value })} className={cn(campo, "w-40")} />
          </label>
          {(dados?.dentistas.length ?? 0) > 1 && (
            <label>
              <span className={rotulo}>Dentista</span>
              <select value={dentistaId ?? ""} onChange={(e) => setDentistaId(e.target.value || null)} className={cn(campo, "w-48")}>
                <option value="">Todos</option>
                {dados!.dentistas.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.nome}
                  </option>
                ))}
              </select>
            </label>
          )}
          {dados && (
            <BotaoExportar
              nome={`extrato-${dados.extrato.destinatarioNome.toLowerCase().replace(/\s+/g, "-")}-${periodo.desde}`}
              titulo="extrato"
              tela="extrato-do-cadastro"
              planilha="extrato"
              quantidade={dados.extrato.itens.length}
              idDe={(i: Item) => i.id}
              buscarLinhas={async () => dados.extrato.itens}
              colunas={[
                { titulo: "Data de entrega", valor: (i) => dataCurta(i.data) },
                { titulo: "Pedido", valor: (i) => i.pedidoNumero },
                { titulo: "Paciente", valor: (i) => i.paciente },
                { titulo: "Dentista", valor: (i) => i.dentista },
                { titulo: "Serviço", valor: (i) => i.descricao },
                { titulo: "Material", valor: (i) => i.material },
                { titulo: "Dentes", valor: (i) => i.dentes },
                { titulo: "Unidades", valor: (i) => i.quantidade },
                { titulo: "Valor unitário", valor: (i) => (i.valorUnitarioCents == null ? null : i.valorUnitarioCents / 100) },
                { titulo: "Valor total", valor: (i) => (i.semPreco ? null : i.valorCents / 100) },
              ]}
            />
          )}
          <button
            type="button"
            onClick={() => window.print()}
            disabled={!dados}
            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
          >
            <Printer className="h-4 w-4" />
            Baixar PDF
          </button>
        </div>
      </div>

      {consulta.error && <p className="py-16 text-center text-sm text-zinc-500">{consulta.error.message}</p>}
      {!dados && !consulta.error && (
        <div className="flex justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
        </div>
      )}
      {dados && (
        <div className="grid gap-4 lg:grid-cols-[1fr_300px] lg:items-start">
          <div data-extrato-impresso className="rounded-xl border border-zinc-200 bg-white p-6 shadow-sm sm:p-10">
            {dados.extrato.itens.length === 0 ? (
              <p className="py-16 text-center text-sm text-zinc-500">Nada entregue a este cliente no período.</p>
            ) : (
              <ExtratoDocumento extrato={dados.extrato} />
            )}
          </div>
          <aside className="rounded-xl border border-zinc-200 bg-white p-4 text-sm shadow-sm dark:border-border dark:bg-card">
            {podeEmitir ? (
              <>
                <p className="font-semibold text-zinc-900 dark:text-foreground">Emitir no Financeiro</p>
                <p className="mt-1 text-xs text-zinc-500 dark:text-muted-foreground">
                  Extrato numerado, com vencimento e recebimentos — e o que entrar nele não é cobrado de novo.
                </p>
                <Link
                  to={`/financeiro?aba=receber&ver=faturar&emitir=${dados.clientOrganizationId}`}
                  className="mt-3 inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-700"
                >
                  Emitir extrato
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </>
            ) : (
              <>
                <p className="font-semibold text-zinc-900 dark:text-foreground">Extrato numerado</p>
                <p className="mt-1 text-xs text-zinc-500 dark:text-muted-foreground">
                  Ligue os extratos em Financeiro › Configurações para emitir extrato numerado, com vencimento e recebimentos, sem cobrar o mesmo trabalho duas vezes.
                </p>
              </>
            )}
            <p className="mt-3 border-t border-zinc-100 pt-3 text-[11px] text-zinc-400 dark:border-border">
              Contatos, formas de pagamento e colunas do documento ficam em{" "}
              <Link to="/financeiro?aba=configuracoes" className="font-semibold text-brand-700 hover:underline dark:text-primary">
                Financeiro › Configurações
              </Link>
              .
            </p>
          </aside>
        </div>
      )}
    </div>
  );
}
